/* The approved Scale surface.
 *
 * Applications import from '@cloudorder/ui' and never from '@telekom/scale-*'
 * directly. Anything an app needs and cannot find here is a deliberate decision
 * to make in this file, not a one-off import in a feature folder.
 *
 * Scale is a Stencil library, so these proxies emit custom elements. Custom
 * events arrive on the React side as onScale{EventName} props. */

/** Compact searchable controls use one shared Scale override so application
 * features do not invent different padding and typography for the same control. */
export const compactComboboxStyles = `
.combobox-container .combobox-input {
  padding-block: 0;
  padding-inline: var(--telekom-spacing-composition-space-05);
  font: var(--telekom-text-style-ui);
}
.combobox-container .combobox-input::placeholder {
  color: var(--telekom-color-text-and-icon-additional);
}
`;

/** Compact single-line fields match the cockpit header's 34px search control
 * while preserving Scale's floating label, focus outline, and validation state. */
export const compactTextFieldStyles = `
.text-field .text-field__control {
  height: 2.125rem;
  min-height: 2.125rem;
  padding-top: 0.8125rem;
  padding-bottom: 0.125rem;
}
.text-field__label {
  transform: translate(var(--spacing-x), 0.5rem);
}
.text-field--has-focus:not(.text-field--readonly) .text-field__label,
.animated .text-field__label {
  font: var(--telekom-text-style-small-bold);
  transform: translate(var(--spacing-x), 0.0625rem);
}
`;

export {
  ScaleLogo,
  ScaleAccordion,
  ScaleBadge,
  ScaleBreadcrumb,
  ScaleButton,
  ScaleCallout,
  ScaleCard,
  ScaleCheckbox,
  ScaleChip,
  ScaleCollapsible,
  ScaleCombobox,
  /* The tabular primitive. Scale's data grid owns sorting, pagination, column
     visibility and the responsive card layout, so applications describe columns
     and rows rather than writing a table. ScaleTable — a bare styling wrapper
     around a table you slot in yourself — is deliberately not exported: two
     table primitives on the approved surface is how a second design system
     starts. */
  ScaleDataGrid,
  ScaleDivider,
  ScaleDropdownSelect,
  ScaleDropdownSelectItem,
  ScaleHelperText,
  ScaleLink,
  ScaleLoadingSpinner,
  ScaleMenuFlyout,
  ScaleMenuFlyoutDivider,
  ScaleMenuFlyoutItem,
  ScaleMenuFlyoutList,
  ScaleModal,
  ScaleNotificationBanner,
  ScaleNotificationMessage,
  ScaleNotificationToast,
  ScalePagination,
  ScaleProgressBar,
  ScaleSegment,
  ScaleSegmentedButton,
  ScaleSwitch,
  ScaleTabHeader,
  ScaleTabNav,
  ScaleTabPanel,
  ScaleTag,
  ScaleTelekomProfileMenu,
  ScaleTextField,
  ScaleTextarea,
  ScaleTooltip,
} from '@telekom/scale-components-react';

/* Icons. The cockpit uses one icon set; these are the members it draws on. */
export {
  ScaleIconActionCheckmark,
  ScaleIconActionClose,
  ScaleIconActionEdit,
  ScaleIconActionExport,
  ScaleIconActionRemove,
  ScaleIconActionFilter,
  ScaleIconActionLightDarkMode,
  ScaleIconActionLink,
  ScaleIconActionMore,
  ScaleIconActionNotification,
  ScaleIconActionRefresh,
  ScaleIconActionSearch,
  ScaleIconActionSend,
  ScaleIconActionShoppingCart,
  ScaleIconActionSuccess,
  ScaleIconAlertError,
  ScaleIconAlertInformation,
  ScaleIconAlertWarning,
  ScaleIconCommunicationChat,
  ScaleIconCommunicationEmail,
  ScaleIconContentClock,
  ScaleIconContentDataVisualisation,
  ScaleIconContentFolder,
  ScaleIconContentHistory,
  ScaleIconContentLock,
  ScaleIconContentRoute,
  ScaleIconContentShop,
  ScaleIconContentSupportChat,
  ScaleIconContentTodoList,
  ScaleIconNavigationCollapseDown,
  ScaleIconNavigationCollapseUp,
  ScaleIconNavigationRight,
  ScaleIconServiceSettings,
  ScaleIconUserFileContacts,
  /* ScaleIconUserFileFile is deliberately absent. Two of Scale's 349 icons paint
     a hard-coded #000 instead of reading the component's own fill — this one
     always, and ScaleIconActionLightDarkMode only in its `selected` state — so
     they vanish on a dark canvas. Contracts is the same document idea, drawn
     with currentColor throughout. */
  ScaleIconUserFileContracts,
  ScaleIconUserFileUser,
} from '@telekom/scale-components-react';
